<script setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { MultiStepModal } from '@flow/components/base';
import { useI18n } from '@/locales';
import { useOnboardingModal } from '@flow/composables';
import DefaultStepLayout from './DefaultStepLayout.vue';
import StartAutomationStep from './StartAutomationStep.vue';

const props = defineProps({
  skipInitShowModal: {
    type: Boolean,
    required: false,
  },
});

const delay = 2000;
const initialStep = 0;
const lastStep = 4;
let timerId = null;

const { t } = useI18n();
const {
  showModal,
  links,
  handleNext,
  handleClose,
  handleCreateTemplate,
  handleCreateFormFlow,
  handleCreateSpreadsheetFlow,
  initShowModal,
} = useOnboardingModal(lastStep);

const steps = [
  {
    component: DefaultStepLayout,
    props: {
      title: t('automation.onboardingModal.welcomeStep.title'),
      content: t('automation.onboardingModal.welcomeStep.content'),
      media: {
        url: links.value?.automation?.introductionAutomation,
      },
    },
    width: 800,
  },
  {
    component: DefaultStepLayout,
    props: {
      icon: 'file-invoice',
      title: t('automation.onboardingModal.templateStep.title'),
      content: t('automation.onboardingModal.templateStep.content'),
      media: {
        url: links.value?.automation?.introductionTemplate,
      },
    },
    width: 720,
  },
  {
    component: DefaultStepLayout,
    props: {
      icon: 'code-branch',
      title: t('automation.onboardingModal.formFlowStep.title'),
      content: t('automation.onboardingModal.formFlowStep.content'),
      media: {
        url: links.value?.automation?.introductionFormFlow,
      },
    },
    width: 720,
  },
  {
    component: DefaultStepLayout,
    props: {
      icon: 'table',
      title: t('automation.onboardingModal.spreadsheetFlowStep.title'),
      content: t('automation.onboardingModal.spreadsheetFlowStep.content'),
      media: {
        url: links.value?.automation?.introductionSpreadsheetFlow,
      },
    },
    width: 720,
  },
  {
    component: StartAutomationStep,
    width: 600,
  },
];

const width = ref(steps[initialStep].width);

onMounted(() => {
  timerId = setTimeout(() => {
    if (!props.skipInitShowModal) {
      initShowModal();
    }
  }, delay);
});

onUnmounted(() => {
  if (timerId) {
    clearTimeout(timerId);
  }
});

const updateWidth = (step) => {
  width.value = steps[step].width;
};

const handleBackButton = ({ nextStep }) => {
  updateWidth(nextStep);
};

const handleNextButton = ({ currentStep, nextStep }) => {
  updateWidth(nextStep);
  handleNext({ currentStep });
};

const handleOpened = () => {
  updateWidth(initialStep);
};
</script>

<template>
  <!--
    TOGGLE_MODAL_INTRODUCTION_AUTOMATION_ENABLED
    TODO: Remove FeatureFlag component and keep MultiStepModal
  -->
  <FeatureFlag name="toggleModalIntroductionAutomationEnabled">
    <MultiStepModal
      :show-modal="showModal"
      :title="$t('automation.onboardingModal.title')"
      :initial-step="initialStep"
      :last-step="lastStep"
      :start-button-text="$t('automation.onboardingModal.welcomeStep.action')"
      :width="width"
      hide-final-button
      @close="handleClose"
      @next="handleNextButton"
      @back="handleBackButton"
      @opened="handleOpened"
    >
      <template #body="{ currentStep }">
        <template v-for="(step, index) in steps">
          <Component
            :is="step.component"
            v-if="currentStep === index"
            :key="index"
            v-bind="step.props"
            @create-template="handleCreateTemplate"
            @create-form-flow="handleCreateFormFlow"
            @create-spreadsheet-flow="handleCreateSpreadsheetFlow"
          />
        </template>
      </template>
    </MultiStepModal>
  </FeatureFlag>
</template>
